// src/hooks/pending-orders/usePendingOrdersForm.ts
// Hook para manejo del formulario de pedidos pendientes

import { useState, useEffect, useCallback } from 'react'
import type { PendingOrderFormData, OrderDetail } from '../../types/pendingOrdersFormTypes'
import { 
  getCiudadNombre, 
  getDepartamentoNombre, 
  transformOrderDetailToFormData 
} from '../../utils/pendingOrdersFormUtils'

interface Departamento {
  Codigo: number | string
  Nombre: string
}

interface Ciudad {
  codigo: string
  ciudad: string
}

interface UsePendingOrdersFormProps {
  orderDetail?: OrderDetail | null
  isLiquidateMode?: boolean
  departamentos: Departamento[]
  ciudades: Ciudad[]
  calculateTotals: (items: any[]) => { total: string }
}

export function usePendingOrdersForm({
  orderDetail,
  isLiquidateMode = false,
  departamentos,
  ciudades,
  calculateTotals
}: UsePendingOrdersFormProps) {
  // Funciones helper con dependencias
  const getCiudadNombreFn = useCallback((codigo: string) => {
    return getCiudadNombre(codigo, ciudades)
  }, [ciudades])

  const getDepartamentoNombreFn = useCallback((codigo: string) => {
    return getDepartamentoNombre(codigo, departamentos)
  }, [departamentos])

  // Estado del formulario
  const [formData, setFormData] = useState<PendingOrderFormData>(() => {
    return transformOrderDetailToFormData(
      orderDetail || null,
      getCiudadNombreFn,
      getDepartamentoNombreFn,
      calculateTotals
    )
  })

  // Estados de expansión de secciones
  const [isOrigenExpanded, setIsOrigenExpanded] = useState(false)
  const [isDestinoExpanded, setIsDestinoExpanded] = useState(true)
  const [isPaqueteExpanded, setIsPaqueteExpanded] = useState(true)
  const [isAdicionalExpanded, setIsAdicionalExpanded] = useState(true)

  // Estados de control del formulario
  const [isFormReady, setIsFormReady] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Handler para cambios en inputs
  const handleInputChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: value
      }

      // Si se cambió el departamento, limpiar la ciudad
      if (name === 'destinoDepartamento') {
        newFormData.destinoCiudad = ''
      }

      return newFormData
    })
  }, [])

  // Handler directo para cambios de campos (sin evento)
  const handleFieldChange = useCallback((field: keyof PendingOrderFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }, [])

  // Efecto para inicializar formData cuando cambia orderDetail
  useEffect(() => {
    if (orderDetail) {
      setIsFormReady(isLiquidateMode)
      setIsInitialLoad(false)
      
      const newFormData = transformOrderDetailToFormData(
        orderDetail,
        getCiudadNombreFn,
        getDepartamentoNombreFn,
        calculateTotals
      )
      
      setFormData(newFormData)
    } else if (isLiquidateMode) {
      setIsFormReady(true)
      setIsInitialLoad(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail?.id, isLiquidateMode])

  return {
    formData,
    setFormData,
    handleInputChange,
    handleFieldChange,
    isOrigenExpanded,
    setIsOrigenExpanded,
    isDestinoExpanded,
    setIsDestinoExpanded,
    isPaqueteExpanded,
    setIsPaqueteExpanded,
    isAdicionalExpanded,
    setIsAdicionalExpanded,
    isFormReady,
    setIsFormReady,
    isInitialLoad,
    setIsInitialLoad
  }
}

